import {
  Box,
  FieldWrapper,
  FormField,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { useCallback, useState } from 'react';
import { v4 as uuid } from 'uuid';

import {
  BasicQueueSelectItem,
  InputQueuesSelect,
} from 'components/inputQueuesSelect';
import {
  FlowNodeTypes,
  OptionT,
} from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import {
  FlowMessengerNodeQueue,
  FlowMessengerNodeQueuePayloadT,
} from 'components/settings/components/sections/dixaMessenger/views/flow/flow.types';
import { Duration } from 'fable/duration';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';
import { ToggleBox, ToggleBoxBody, ToggleBoxHead } from 'fable/toggleBox';

import styles from './queueModal.module.scss';

const QUEUE_FIELD = 'queue';
const QUEUE_TIMEOUT_FIELD = 'queue_timeout';

type Props = FlowManagerEditModalComponentT<FlowMessengerNodeQueue>;

type Errors = {
  [Key in typeof QUEUE_FIELD | typeof QUEUE_TIMEOUT_FIELD]?: string;
};

export const QueueModal = ({
  node,
  nodeLabel,
  replaceNode,
  triggerClose,
}: Props): JSX.Element => {
  const { payload } = node;
  const [errors, setErrors] = useState<Errors>({});
  const [{ queue, queue_timeout }, setState] =
    useState<FlowMessengerNodeQueuePayloadT>({
      queue: null,
      ...payload,
    });

  const isChanged = useIsChanged(
    [payload?.queue, queue],
    [payload?.queue_timeout, queue_timeout],
  );

  const validate = useCallback((): boolean => {
    const errors: Errors = {};

    if (!queue?.value) {
      errors[QUEUE_FIELD] = 'Selecting a queue is required';
    }

    if (queue_timeout && !queue_timeout.timeout_in_seconds) {
      errors[QUEUE_TIMEOUT_FIELD] = 'Please set a timeout value';
    }

    setErrors(errors);

    return Object.keys(errors).length === 0;
  }, [queue, queue_timeout]);

  const handleSaveClick = useCallback((): void => {
    if (!validate()) {
      return;
    }

    const newPayload = {
      ...node.payload,
      queue: queue as OptionT<string>,
      queue_timeout,
    };

    if (!queue_timeout) {
      delete newPayload.queue_timeout;
    }

    const nodeOptions: FlowMessengerNodeQueue = {
      ...node,
      badges: [],
      childIds: [],
      icon: '',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.QUEUE,
      overlay: [],
      payload: newPayload,
      text: '',
    };

    replaceNode(nodeOptions);
    triggerClose();
  }, [node, replaceNode, queue, queue_timeout, triggerClose, validate]);

  const handleQueueChange = useCallback(
    (queue?: BasicQueueSelectItem): void => {
      if (!queue) {
        return;
      }

      setState((state) => ({
        ...state,
        queue: {
          value: queue.id,
          label: queue.name,
        },
      }));
    },
    [],
  );

  const handleToggleTimeout = useCallback((isOpen: boolean): void => {
    setState(({ queue_timeout, ...state }) => {
      if (isOpen) {
        return {
          ...state,
          queue_timeout: {
            timeout_in_seconds: 0,
            timeout_node_id: queue_timeout?.timeout_node_id || uuid(),
          },
        };
      }

      return state;
    });
  }, []);

  const handleTimeoutChange = useCallback((timeoutInSeconds: number): void => {
    setState((state) => ({
      ...state,
      queue_timeout: {
        timeout_in_seconds: timeoutInSeconds,
        timeout_node_id: state.queue_timeout?.timeout_node_id || uuid(),
      },
    }));
  }, []);

  return (
    <Box width="600px">
      <ModalHead title={nodeLabel} onClose={triggerClose}>
        <div className={styles.description}>
          The queue is where you’d like your incoming conversations to end up
          based on the queues you’ve set up. Haven’t made any custom queues yet?{' '}
          Go to Settings &#62; Queues &#62; Add queue
        </div>
      </ModalHead>
      <form onSubmit={handleSaveClick}>
        <ModalBody>
          <FieldWrapper label={'Select queue'} required>
            <FormField>
              <InputQueuesSelect
                required
                placeholder="No queue selected"
                value={queue ? [{ id: queue.value, name: queue.label }] : []}
                onChange={(e) => handleQueueChange(e.selectedValues[0])}
                status={errors[QUEUE_FIELD] ? 'error' : undefined}
              />
            </FormField>
          </FieldWrapper>
          <ToggleBox
            isOpen={!!queue_timeout}
            headComponent={ToggleBoxHead}
            title="Timeout"
            input={{
              id: 'timeout',
              name: 'timeout',
              value: 'timeout',
            }}
            helpIcon={{
              title: 'Timeout',
              text: 'Select how many seconds the customer should wait in line before a new action is triggered.',
            }}
            onChange={handleToggleTimeout}
          >
            <ToggleBoxBody>
              <Text.Paragraph className={styles.timeoutDescription} subdued>
                Select how long the customer should wait before a new action is
                triggered.
              </Text.Paragraph>
              <Duration
                duration={queue_timeout?.timeout_in_seconds}
                label="Set a timeout"
                meta={{ error: errors[QUEUE_TIMEOUT_FIELD] }}
                onChange={handleTimeoutChange}
              />
            </ToggleBoxBody>
          </ToggleBox>
        </ModalBody>
        <ModalFoot
          confirmOnClick={handleSaveClick}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{ disabled: !isChanged }}
          cancelOnClick={triggerClose}
        />
      </form>
    </Box>
  );
};
